/**
 * Packs — 5-Card Booster Pack Opening System.
 * 
 * Rules:
 * - Cost: 150 Fugazi per pack.
 * - Exactly 5 cards per pack.
 * - Draws strictly from the 12 active playable units.
 * - Drop Table per slot:
 *   - ~88% Standard Card (already owned / Niete)
 *   - ~10% Slot Machine Ticket
 *   - ~2% Special Edition Card (e.g. Gold Marine)
 */

import { CARDS_DATA, getGoldCardImage } from '../data/cards.js';
import { wallet } from './wallet.js';
import { inventory } from './inventory.js';

export const PACK_COST_FUGAZI = 150;
export const CARDS_PER_PACK = 5;
// Opening this many packs at once (the maximum batch) always yields at least one Slot Ticket.
export const GUARANTEED_TICKET_PACK_COUNT = 5;

export const DROP_RATES = {
  SPECIAL_EDITION: 0.02, // 2%
  TICKET: 0.10,          // 10%
  STANDARD: 0.88         // 88%
};

export const PACK_DEFINITIONS = {
  core_pack: {
    id: 'core_pack',
    name: 'Core Booster',
    cost: PACK_COST_FUGAZI,
    cardsCount: CARDS_PER_PACK,
    race: null,
    themeColor: '#f59e0b',
    description: 'CONTAINS ALL FACTIONS'
  },
  human_pack: {
    id: 'human_pack',
    name: 'Marine Booster',
    cost: PACK_COST_FUGAZI,
    cardsCount: CARDS_PER_PACK,
    race: 'Human',
    themeColor: '#00e5ff',
    description: 'EXCLUSIVELY HUMAN FORCES'
  },
  sul_pack: {
    id: 'sul_pack',
    name: 'Swarm Booster',
    cost: PACK_COST_FUGAZI,
    cardsCount: CARDS_PER_PACK,
    race: 'Sul',
    themeColor: '#d946ef',
    description: 'EXCLUSIVELY SUL FORCES'
  }
};

class PackManager {
  constructor() {
    this._activePool = null;
  }

  getActiveCardPool(race = null) {
    if (race) {
      return CARDS_DATA.filter(c => c.enabled !== false && !c.isCampaignOnly && c.race === race);
    }
    if (!this._activePool) {
      this._activePool = CARDS_DATA.filter(c => c.enabled !== false && !c.isCampaignOnly);
    }
    return this._activePool;
  }

  getSpecialEditionPool(race = null) {
    if (race) {
      return CARDS_DATA.filter(c => (c.enabled === true || c.type === 'Melt') && !c.isCampaignOnly && c.race === race);
    }
    return CARDS_DATA.filter(c => (c.enabled === true || c.type === 'Melt') && !c.isCampaignOnly);
  }

  canAffordPack(packId = 'core_pack') {
    const pack = PACK_DEFINITIONS[packId];
    if (!pack) return false;
    return wallet.getFugazi() >= pack.cost;
  }

  /**
   * Opens a 5-card booster pack.
   * Consumes an inventory booster pack if { free: true } is passed,
   * otherwise deducts 150 Fugazi. Rolls 5 cards, grants tickets/special editions.
   * All wallet/inventory mutations are persisted with one write per store. { deferNotify: true }
   * additionally holds back the change listeners until flushNotifications() (the Cargo Bay calls
   * it once the tear animation is over, so the fan-out does not land on the animation's first frame).
   */
  openPack(packId = 'core_pack', { free = false, deferNotify = false } = {}) {
    return this._batched(deferNotify, () => this._openPack(packId, free));
  }

  _batched(deferNotify, fn) {
    wallet.beginBatch();
    inventory.beginBatch();
    try {
      return fn();
    } finally {
      wallet.endBatch({ notify: !deferNotify });
      inventory.endBatch({ notify: !deferNotify });
    }
  }

  /** Runs the listeners held back by a { deferNotify: true } opening. Safe to call any time. */
  flushNotifications() {
    wallet.flushNotify();
    inventory.flushNotify();
  }

  _openPack(packId, free) {
    const pack = PACK_DEFINITIONS[packId] || PACK_DEFINITIONS.core_pack;

    let usedFreePack = false;
    if (free && inventory.getBoosterPacks() > 0) {
      usedFreePack = inventory.consumeBoosterPack();
    }

    if (!usedFreePack) {
      if (wallet.getFugazi() < pack.cost) {
        return {
          success: false,
          reason: 'insufficient_fugazi',
          required: pack.cost,
          available: wallet.getFugazi()
        };
      }

      const spent = wallet.spendFugazi(pack.cost, 'booster_pack');
      if (!spent) {
        return {
          success: false,
          reason: 'transaction_failed'
        };
      }
    }

    const pool = this.getActiveCardPool(pack.race);
    const pulledCards = [];
    let ticketsWon = 0;
    let specialCardsWon = 0;

    for (let i = 0; i < CARDS_PER_PACK; i++) {
      const roll = Math.random();

      if (roll < DROP_RATES.SPECIAL_EDITION) {
        // Gold / Special Edition
        const specialPool = this.getSpecialEditionPool(pack.race);
        const cardRef = specialPool[Math.floor(Math.random() * specialPool.length)];
        const isNew = inventory.addSpecialCard(cardRef.name, 'gold');
        specialCardsWon++;

        pulledCards.push({
          slotIndex: i,
          type: 'special_edition',
          edition: 'gold',
          cardName: cardRef.name,
          title: `${cardRef.name} (Gold Edition)`,
          subtitle: isNew ? '★ NEW DISCOVERY! ★' : 'ALREADY OWNED',
          description: `Exclusive holographic gold special edition of ${cardRef.name}.`,
          rarity: 'Mythic Gold',
          image: getGoldCardImage(cardRef.name) || cardRef.images?.[0] || 'card_marine.png',
          cost: cardRef.cost,
          isNew
        });
      } else if (roll < (DROP_RATES.SPECIAL_EDITION + DROP_RATES.TICKET)) {
        // Slot Machine Ticket
        wallet.addTickets(1, 'pack_drop');
        ticketsWon++;

        pulledCards.push({
          slotIndex: i,
          type: 'ticket',
          title: 'Slot Machine Ticket',
          subtitle: '★ TICKET DRAWN! ★',
          description: 'Grants one spin on the 3-reel cosmetic slot machine.',
          rarity: 'Cyber Rare',
          image: 'ticket_icon',
          isNew: true
        });
      } else {
        // Standard Card (already owned / Niete)
        const cardRef = pool[Math.floor(Math.random() * pool.length)];

        pulledCards.push({
          slotIndex: i,
          type: 'standard',
          cardName: cardRef.name,
          title: cardRef.name,
          subtitle: 'ALREADY OWNED',
          description: cardRef.description || 'Standard Combat Edition.',
          rarity: cardRef.rarity || 'Common',
          image: cardRef.images?.[0] || 'card_marine.png',
          cost: cardRef.cost,
          isNew: false
        });
      }
    }

    inventory.recordPackOpened(ticketsWon, specialCardsWon);

    return {
      success: true,
      packId: pack.id,
      packName: pack.name,
      race: pack.race,
      cards: pulledCards,
      ticketsWon,
      specialCardsWon,
      remainingFugazi: wallet.getFugazi(),
      totalTickets: wallet.getTickets()
    };
  }

  /**
   * Opens multiple booster packs (1 to 5) in a single batch.
   * Priority: Always consumes free inventory booster packs first,
   * then deducts 150 Fugazi per remaining pack from the wallet.
   *
   * @param {string} packId - ID of the booster pack (core_pack, human_pack, sul_pack)
   * @param {number} count - Desired number of packs to open (clamped to 1..5)
   * @returns {Object} Batch result containing all pulled cards and summary metrics
   */
  openPackBatch(packId = 'core_pack', count = 1, { deferNotify = false } = {}) {
    return this._batched(deferNotify, () => this._openPackBatch(packId, count));
  }

  _openPackBatch(packId, count) {
    const pack = PACK_DEFINITIONS[packId] || PACK_DEFINITIONS.core_pack;
    const n = Math.max(1, Math.min(Math.floor(count) || 1, 5));

    const freeAvailable = inventory.getBoosterPacks();
    const freeToConsume = Math.min(n, freeAvailable);
    const fugaziPacksToBuy = n - freeToConsume;
    const requiredFugazi = fugaziPacksToBuy * pack.cost;

    if (wallet.getFugazi() < requiredFugazi) {
      return {
        success: false,
        reason: 'insufficient_fugazi',
        required: requiredFugazi,
        available: wallet.getFugazi(),
        freeAvailable
      };
    }

    // Deduct free packs from inventory first
    let freeUsed = 0;
    for (let i = 0; i < freeToConsume; i++) {
      if (inventory.consumeBoosterPack()) {
        freeUsed++;
      }
    }

    // Deduct remaining Fugazi cost
    let fugaziSpent = 0;
    if (requiredFugazi > 0) {
      const spent = wallet.spendFugazi(requiredFugazi, 'booster_pack_batch');
      if (!spent) {
        return {
          success: false,
          reason: 'transaction_failed'
        };
      }
      fugaziSpent = requiredFugazi;
    }

    const pool = this.getActiveCardPool(pack.race);
    const specialPool = this.getSpecialEditionPool(pack.race);
    const allPulledCards = [];
    let totalTicketsWon = 0;
    let totalSpecialCardsWon = 0;

    for (let p = 0; p < n; p++) {
      for (let i = 0; i < CARDS_PER_PACK; i++) {
        const slotIndex = p * CARDS_PER_PACK + i;
        const roll = Math.random();

        if (roll < DROP_RATES.SPECIAL_EDITION) {
          // Gold / Special Edition
          const cardRef = specialPool[Math.floor(Math.random() * specialPool.length)];
          const isNew = inventory.addSpecialCard(cardRef.name, 'gold');
          totalSpecialCardsWon++;

          allPulledCards.push({
            slotIndex,
            packIndex: p,
            type: 'special_edition',
            edition: 'gold',
            cardName: cardRef.name,
            title: `${cardRef.name} (Gold Edition)`,
            subtitle: isNew ? '★ NEW DISCOVERY! ★' : 'ALREADY OWNED',
            description: `Exclusive holographic gold special edition of ${cardRef.name}.`,
            rarity: 'Mythic Gold',
            image: getGoldCardImage(cardRef.name) || cardRef.images?.[0] || 'card_marine.png',
            cost: cardRef.cost,
            isNew
          });
        } else if (roll < (DROP_RATES.SPECIAL_EDITION + DROP_RATES.TICKET)) {
          // Slot Machine Ticket
          wallet.addTickets(1, 'pack_drop');
          totalTicketsWon++;

          allPulledCards.push({
            slotIndex,
            packIndex: p,
            type: 'ticket',
            title: 'Slot Machine Ticket',
            subtitle: '★ TICKET DRAWN! ★',
            description: 'Grants one spin on the 3-reel cosmetic slot machine.',
            rarity: 'Cyber Rare',
            image: 'ticket_icon',
            isNew: true
          });
        } else {
          // Standard Card (already owned / Niete)
          const cardRef = pool[Math.floor(Math.random() * pool.length)];

          allPulledCards.push({
            slotIndex,
            packIndex: p,
            type: 'standard',
            cardName: cardRef.name,
            title: cardRef.name,
            subtitle: 'ALREADY OWNED',
            description: cardRef.description || 'Standard Combat Edition.',
            rarity: cardRef.rarity || 'Common',
            image: cardRef.images?.[0] || 'card_marine.png',
            cost: cardRef.cost,
            isNew: false
          });
        }
      }
    }

    // Full-batch guarantee: the roll can come up without a single ticket (~7% for 25 cards); a batch of
    // GUARANTEED_TICKET_PACK_COUNT packs then turns one random standard card into a ticket, granted like
    // any other ticket drop. Gold cards are never touched.
    let ticketGuaranteed = false;
    if (n >= GUARANTEED_TICKET_PACK_COUNT && totalTicketsWon === 0) {
      const standardAt = [];
      allPulledCards.forEach((c, i) => { if (c.type === 'standard') standardAt.push(i); });
      if (standardAt.length) {
        const at = standardAt[Math.floor(Math.random() * standardAt.length)];
        const old = allPulledCards[at];
        wallet.addTickets(1, 'pack_guarantee');
        totalTicketsWon++;
        ticketGuaranteed = true;
        allPulledCards[at] = {
          slotIndex: old.slotIndex,
          packIndex: old.packIndex,
          type: 'ticket',
          title: 'Slot Machine Ticket',
          subtitle: '★ TICKET DRAWN! ★',
          description: 'Grants one spin on the 3-reel cosmetic slot machine.',
          rarity: 'Cyber Rare',
          image: 'ticket_icon',
          isNew: true
        };
      }
    }

    inventory.recordPackOpened(totalTicketsWon, totalSpecialCardsWon);

    return {
      success: true,
      packId: pack.id,
      packName: pack.name,
      race: pack.race,
      packCount: n,
      ticketGuaranteed,
      cards: allPulledCards,
      ticketsWon: totalTicketsWon,
      specialCardsWon: totalSpecialCardsWon,
      freePacksUsed: freeUsed,
      fugaziSpent,
      remainingFugazi: wallet.getFugazi(),
      totalTickets: wallet.getTickets()
    };
  }
}

export const packs = new PackManager();
export { PackManager };
